/**
 * AI spend (day 37, PRD sections 4 and 5.9): what the AI tools cost, by tool
 * and by day, which models each tool is on, and the monthly level at which
 * Mohit is emailed (D15, Rs 100 until changed here).
 *
 * Billed is what the providers charge. Workers AI inside its free 10,000
 * neurons a day bills nothing, so until day 97's paid models the billed figure
 * stays at nothing while the list price says what the same calls would cost.
 * Choosing a tool's models from here is day 81's; the API takes the setting
 * already (PUT /api/admin/ai/tools/:tool).
 */

import { useEffect, useState } from "preact/hooks";
import type { AiSpendResponse, AiSpendRow } from "@ls/shared";
import { ApiUnavailable, http } from "../../web/src/api";
import { adminErrorContract, aiSettingResponseContract, aiSpendResponseContract } from "../../web/src/contracts.generated";
import { Button, Card, EmptyState, Field, PageHeader, StatStrip, Table } from "../../web/src/ui";

/** Rupees from millionths of a dollar, at the catalogue's rate. A cost too small to show as paise says so. */
export function rupees(microusd: number, fxRate: number): string {
  const inr = (microusd / 1_000_000) * fxRate;
  if (microusd > 0 && inr < 0.005) return "under Rs 0.01";
  return `Rs ${inr.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function paise(value: number): string {
  return `Rs ${(value / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const PROVIDER_NAMES: Readonly<Record<string, string>> = { "workers-ai": "Workers AI", anthropic: "Anthropic", google: "Google", openai: "OpenAI" };

/** "workers-ai:@cf/google/gemma-4-26b-a4b-it" as "Workers AI, gemma-4-26b-a4b-it". */
export function modelName(ref: string): string {
  const at = ref.indexOf(":");
  const provider = ref.slice(0, at);
  const model = ref.slice(at + 1).split("/").at(-1) ?? ref;
  return `${PROVIDER_NAMES[provider] ?? provider}, ${model}`;
}

interface Totals {
  calls: number;
  failed: number;
  tokens: number;
  neurons: number;
  list: number;
  billed: number;
}

/** The rows added up by one of their columns, in first seen order. */
export function totalsBy(rows: readonly AiSpendRow[], key: "day" | "tool"): [string, Totals][] {
  const out = new Map<string, Totals>();
  for (const row of rows) {
    const at = out.get(row[key]) ?? { calls: 0, failed: 0, tokens: 0, neurons: 0, list: 0, billed: 0 };
    at.calls += row.calls;
    at.failed += row.failed;
    at.tokens += row.tokensIn + row.tokensOut;
    at.neurons += row.neurons;
    at.list += row.listMicrousd;
    at.billed += row.billedMicrousd;
    out.set(row[key], at);
  }
  return [...out.entries()];
}

/** "2026-10" as "October 2026". */
export function monthName(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}

function day(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

type Load = { readonly kind: "loading" } | { readonly kind: "ready"; readonly spend: AiSpendResponse } | { readonly kind: "failed"; readonly message: string };

export function Spend() {
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [level, setLevel] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ readonly bad: boolean; readonly text: string } | null>(null);

  const read = async () => {
    try {
      const reply = await http.get("/admin/ai/spend");
      const parsed = aiSpendResponseContract.safeParse(reply.body);
      if (reply.status === 200 && parsed.success) {
        setLoad({ kind: "ready", spend: parsed.data });
        setLevel(String(parsed.data.month.thresholdInr));
        return;
      }
      const problem = adminErrorContract.safeParse(reply.body);
      setLoad({ kind: "failed", message: problem.success ? problem.data.message : "The spend could not be read." });
    } catch (error) {
      setLoad({ kind: "failed", message: error instanceof ApiUnavailable ? "The server could not be reached." : "The spend could not be read." });
    }
  };

  useEffect(() => {
    void read();
  }, []);

  const save = async () => {
    const inr = Number(level);
    if (!Number.isInteger(inr) || inr < 1) {
      setNotice({ bad: true, text: "Give the level in whole rupees, at least 1." });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const reply = await http.put("/admin/ai/alert", { inr });
      if (reply.status === 200 && aiSettingResponseContract.safeParse(reply.body).success) {
        setNotice({ bad: false, text: `Saved. The email goes the first time a month passes Rs ${inr.toLocaleString("en-IN")}.` });
        await read();
      } else {
        const problem = adminErrorContract.safeParse(reply.body);
        setNotice({ bad: true, text: problem.success ? problem.data.message : `The server answered ${reply.status}.` });
      }
    } catch (error) {
      setNotice({ bad: true, text: error instanceof ApiUnavailable ? "The server could not be reached." : "That did not go through." });
    } finally {
      setBusy(false);
    }
  };

  if (load.kind === "loading") {
    return (
      <div class="spend">
        <PageHeader title="AI spend" />
        <p class="note">Loading...</p>
      </div>
    );
  }
  if (load.kind === "failed") {
    return (
      <div class="spend">
        <PageHeader title="AI spend" />
        <p class="notice bad" role="alert">
          {load.message}
        </p>
      </div>
    );
  }

  const { spend } = load;
  const fx = spend.month.fxRate;
  const byTool = totalsBy(spend.days, "tool");
  const byDay = totalsBy(spend.days, "day");

  return (
    <div class="spend">
      <PageHeader title="AI spend" />
      <StatStrip
        label="This month"
        stats={[
          { label: `Billed in ${monthName(spend.month.month)}`, value: paise(spend.month.billedPaise), note: "What the providers charge" },
          { label: "At list price", value: paise(spend.month.listPaise), note: "The same calls on paid plans" },
          {
            label: "Workers AI neurons today",
            value: Math.round(spend.neuronsToday).toLocaleString("en-IN"),
            note: `Of ${spend.neuronCeiling.toLocaleString("en-IN")} the switch allows; resets 05:30 IST`,
          },
        ]}
      />

      <Card title="Monthly email">
        <form
          class="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <p class="note">
            {spend.month.alertedAt
              ? `This month passed the level and the email went on ${new Date(spend.month.alertedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}.`
              : `Not passed this month. One email goes the first time a month's billed spend passes the level; nothing is ever stopped.`}
          </p>
          <Field id="spendLevel" label="Email when a month passes (rupees)" type="number" value={level} disabled={busy} onInput={setLevel} />
          <div>
            <Button variant="primary" type="submit" disabled={busy}>
              {busy ? "Saving..." : "Save"}
            </Button>
          </div>
          {notice ? (
            <p class={notice.bad ? "notice bad" : "notice"} role={notice.bad ? "alert" : "status"} data-spend-notice>
              {notice.text}
            </p>
          ) : null}
        </form>
      </Card>

      <Table
        caption="Models by tool"
        columns={[
          { key: "tool", label: "Tool" },
          { key: "feature", label: "Serves" },
          { key: "primary", label: "Primary" },
          { key: "standby", label: "Standby" },
          { key: "source", label: "Set by" },
        ]}
        rows={spend.tools.map((tool) => ({
          tool: <span data-ai-tool={tool.tool}>{tool.tool}</span>,
          feature: `${tool.feature}, day ${tool.ships}`,
          primary: modelName(tool.primary),
          standby: modelName(tool.standby),
          source: tool.source === "setting" ? "This site" : "Default",
        }))}
      />

      {spend.days.length === 0 ? (
        <Card>
          <EmptyState title="No AI calls in the last 31 days." line="Each call to a model is recorded here with its tokens and cost." />
        </Card>
      ) : (
        <>
          <Table
            caption="By tool, last 31 days"
            columns={[
              { key: "tool", label: "Tool" },
              { key: "calls", label: "Calls", numeric: true },
              { key: "failed", label: "Failed", numeric: true },
              { key: "tokens", label: "Tokens", numeric: true },
              { key: "list", label: "List price", numeric: true },
              { key: "billed", label: "Billed", numeric: true },
            ]}
            rows={byTool.map(([tool, t]) => ({
              tool,
              calls: t.calls.toLocaleString("en-IN"),
              failed: t.failed.toLocaleString("en-IN"),
              tokens: t.tokens.toLocaleString("en-IN"),
              list: rupees(t.list, fx),
              billed: rupees(t.billed, fx),
            }))}
          />
          <Table
            caption="By day (IST)"
            columns={[
              { key: "day", label: "Day" },
              { key: "calls", label: "Calls", numeric: true },
              { key: "failed", label: "Failed", numeric: true },
              { key: "neurons", label: "Neurons", numeric: true },
              { key: "list", label: "List price", numeric: true },
              { key: "billed", label: "Billed", numeric: true },
            ]}
            rows={byDay.map(([iso, t]) => ({
              day: day(iso),
              calls: t.calls.toLocaleString("en-IN"),
              failed: t.failed.toLocaleString("en-IN"),
              neurons: Math.round(t.neurons).toLocaleString("en-IN"),
              list: rupees(t.list, fx),
              billed: rupees(t.billed, fx),
            }))}
          />
        </>
      )}
    </div>
  );
}
